import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useEffect, useState } from 'react';

import { useScrollPosition } from '@hooks/useScrollPosition';
import { Spacing } from 'chatie-design-system';

import EmptyChatRoom, { ChatRoomInfo } from '@components/chat/EmptyChatRoom';
import Empty from '@components/common/Empty';
import HorizontalFilter from '@components/common/HorizontalFilter';
import { LinkItem } from '@components/common/ListItem';
import NativeInFeedAd from '@components/common/NativeInFeedAd';
import { PaginationIterator } from '@components/common/Pagination';
import { PushSize } from '@components/layout/Push';
import LikedStory from '@components/stories/LikedStory';

import {
  CharacterQueryParams,
  fetchCharacters,
  fetchSituations,
} from '@api/chatbot';
import { fetchLikedStories } from '@api/story';
import { Platform, QUERY_KEYS, StoryTheme } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { sticky } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import { setFlow } from '@utils/misc';
import { useChatPaginationQuery, usePaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

type ActiveType = 'story' | 'chatbot';

const filterCSS = (theme: Theme) => css`
  display: flex;
  flex-direction: row;
  text-align: left;
  padding: ${Spacing.small}px ${Spacing.medium}px;
  border-bottom: 1px solid ${theme.border.light};
  ${sticky(PushSize.tab)};
  z-index: 3;
  background-color: ${theme.var.white};
  height: 43px;
  gap: ${Spacing.xsmall}px;

  &::after {
    content: '';
    display: table;
    clear: both;
  }
`;

const chatRoomListCss = css`
  margin-top: ${Spacing.small}px;
`;

const LikedStoryList = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const [activeType, setActiveType] = useState<ActiveType>('story');

  const { firebase } = useAnalytics();
  const { platform } = useGlobalContext();
  const { web } = useWebView();

  const isChatbot = activeType === 'chatbot';
  const isStory = activeType === 'story';

  const query = usePaginationQuery(
    QUERY_KEYS.LIKED_STORY.LIST,
    ({ pageParam }) => fetchLikedStories(pageParam)
  );

  useScrollPosition('likedStoryList');

  const characterQueries: CharacterQueryParams = {
    only_like: true,
  };

  const characterQueryKey = QUERY_KEYS.CHARACTER.LIKE_LIST();

  const characterQuery = useChatPaginationQuery(
    characterQueryKey,
    async ({ pageParam }) => await fetchCharacters(characterQueries, pageParam),
    {
      refetchOnWindowFocus: 'always',
    }
  );

  const handleClickMoveToIntro = async (item: ChatRoomInfo) => {
    setFlow({ name: '대화하기' });
    const isOfficial = item?.type === 'official';

    const characterId = item.id;
    firebase.logEvent('chatbot_click_library_like_chat', {
      btn_type: 'chat',
      character_id: characterId,
    });

    if (isOfficial) {
      if (platform === Platform.web) {
        return router.push(`/play/characters/${characterId}`);
      } else {
        return web.openPage(`/play/characters/${characterId}`);
      }
    }
    const situations = await fetchSituations({ character_id: item.id });
    if (!situations?.items?.length) {
      if (platform === Platform.web) {
        return router.push(`/play/characters/${characterId}`);
      } else {
        return web.openPage(`/play/characters/${characterId}`);
      }
    }

    if (platform === Platform.web) {
      return router.push(
        `/play/situations/${situations?.items[0]?.id}/chat-room`
      );
    } else {
      return web.openPage(
        `/play/situations/${situations?.items[0]?.id}/chat-room`
      );
    }
  };

  const handleClickAvatar = (characterId: number) => {
    firebase.logEvent('chatbot_click_library_like_chat', {
      btn_type: 'profile',
      character_id: characterId,
    });
    if (platform === Platform.web) {
      return router.push(`/play/characters/${characterId}`);
    } else {
      return web.openPage(`/play/characters/${characterId}`);
    }
  };

  useEffect(() => {
    if (isChatbot) {
      setFlow({ isDelete: true });
      setFlow({ name: '보관함/좋아요' });
    }
  }, [isChatbot]);

  return (
    <>
      <div css={filterCSS}>
        <HorizontalFilter
          options={[
            {
              label: `전체(${
                isStory
                  ? query.data?.pages[0].totalCount
                  : characterQuery.totalCount
              })`,
              value: 'latest',
            },
          ]}
        />
        <HorizontalFilter
          options={[
            { label: t('작품'), value: 'story' },
            { label: t('캐릭터'), value: 'chatbot' },
          ]}
          selected={activeType}
          onChange={setActiveType}
        />
      </div>
      {isStory && (
        <PaginationIterator
          name="liked-story-list"
          query={query}
          border
          inFeedAd={<NativeInFeedAd unitKey="story_mobile" />}
        >
          {(item) => (
            <LinkItem
              to={
                item.theme === StoryTheme.iknow
                  ? `/stories/${item.id}/licensed`
                  : `/stories/${item.id}`
              }
              newTab
              hideArrow
              left={<LikedStory {...item} />}
            />
          )}
        </PaginationIterator>
      )}
      {isChatbot && (
        <PaginationIterator
          name="liked-character-list"
          query={characterQuery}
          inFeedAd={<NativeInFeedAd unitKey="library_mobile" />}
        >
          {(item) => {
            const props = {
              characterName: item.name,
              image: item.profile_image,
              id: item.id,
              lastMessage: item.greeting,
              type: item.type,
            };

            return (
              <div css={chatRoomListCss} key={item.id}>
                <LinkItem
                  hideArrow
                  newTab
                  left={
                    <EmptyChatRoom
                      data={props}
                      onClickAvatar={handleClickAvatar}
                      onClickEnter={handleClickMoveToIntro}
                      buttonText={'대화하기'}
                    />
                  }
                />
              </div>
            );
          }}
        </PaginationIterator>
      )}
      <Empty query={characterQuery}>
        <p>{t('좋아요한 캐릭터가 아직 없어요.')}</p>
      </Empty>
    </>
  );
};

export default LikedStoryList;
